import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DollarSign,
  TrendingUp,
  FileText,
  Car,
  RefreshCw,
  Plus,
  Send,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Settings,
  Download,
  Eye,
  CreditCard,
  Calendar,
  MapPin,
  Filter,
  ChevronDown,
  Zap,
  PieChart,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status colors
const invoiceStatusColors = {
  draft: { bg: 'bg-slate-500/20', text: 'text-slate-400', label: 'Draft' },
  sent: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Sent' },
  paid: { bg: 'bg-neon-green/20', text: 'text-neon-green', label: 'Paid' },
  partially_paid: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Partial' },
  overdue: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Overdue' },
  cancelled: { bg: 'bg-slate-600/20', text: 'text-slate-500', label: 'Cancelled' }
};

const tripTypeColors = {
  wheelchair: { bg: 'bg-blue-500/20', text: 'text-blue-400' },
  ambulatory: { bg: 'bg-green-500/20', text: 'text-green-400' },
  stretcher: { bg: 'bg-purple-500/20', text: 'text-purple-400' },
  bariatric: { bg: 'bg-orange-500/20', text: 'text-orange-400' }
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subValue, color = 'neon-cyan', trend }) => (
  <div className="glass-card p-4">
    <div className="flex items-start justify-between">
      <div className={`w-10 h-10 rounded-lg bg-${color}/20 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}`} />
      </div>
      {trend && (
        <span className={`text-xs px-2 py-0.5 rounded ${trend > 0 ? 'bg-neon-green/20 text-neon-green' : 'bg-red-500/20 text-red-400'}`}>
          {trend > 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
    <div className="mt-3">
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-sm text-slate-400">{label}</div>
      {subValue && <div className="text-xs text-slate-500 mt-1">{subValue}</div>}
    </div>
  </div>
);

// Trip Row Component
const TripRow = ({ trip }) => (
  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/8 transition-colors">
    <div className="flex items-center space-x-3">
      <div className={`w-8 h-8 rounded-lg ${tripTypeColors[trip.trip_type]?.bg || 'bg-slate-600'} flex items-center justify-center`}>
        <Car className={`w-4 h-4 ${tripTypeColors[trip.trip_type]?.text || 'text-slate-400'}`} />
      </div>
      <div>
        <div className="text-white text-sm font-medium">Ride #{trip.ride_id}</div>
        <div className="text-xs text-slate-400">
          {trip.pickup_zip} → {trip.dropoff_zip}
        </div>
      </div>
    </div>
    <div className="flex items-center space-x-4">
      <div className="text-right">
        <div className="text-sm text-white">{trip.miles_charged?.toFixed(1)} mi</div>
        <div className="text-xs text-slate-400">${trip.rate_per_mile}/mi</div>
      </div>
      <div className="text-right min-w-[70px]">
        <div className={`text-sm font-medium ${trip.billable ? 'text-neon-green' : 'text-slate-500'}`}>
          ${trip.insurance_charge_usd?.toFixed(2)}
        </div>
        <div className="text-xs text-slate-500">
          {trip.billable ? 'Billable' : 'Not Billed'}
        </div>
      </div>
    </div>
  </div>
);

// Invoice Row Component
const InvoiceRow = ({ invoice, onView, onPay, onSend }) => {
  const status = invoiceStatusColors[invoice.status] || invoiceStatusColors.draft;
  
  return (
    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/8 transition-colors">
      <div className="flex items-center space-x-3">
        <FileText className="w-5 h-5 text-slate-400" />
        <div>
          <div className="text-white text-sm font-medium">{invoice.invoice_number}</div>
          <div className="text-xs text-slate-400">
            {invoice.total_trips} trips · {invoice.total_miles?.toFixed(1)} mi
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-4">
        <div className="text-right">
          <div className="text-sm text-white font-medium">${invoice.total_usd?.toFixed(2)}</div>
          <div className="text-xs text-slate-400">Due {invoice.due_date}</div>
        </div>
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${status.bg} ${status.text}`}>
          {status.label}
        </span>
        <div className="flex space-x-1">
          <button
            onClick={() => onView(invoice)}
            className="p-1.5 rounded bg-white/5 hover:bg-white/10 transition-colors"
            title="View Details"
          >
            <Eye className="w-4 h-4 text-slate-400" />
          </button>
          {invoice.status === 'draft' && (
            <button
              onClick={() => onSend(invoice)}
              className="p-1.5 rounded bg-blue-500/20 hover:bg-blue-500/30 transition-colors"
              title="Send Invoice"
            >
              <Send className="w-4 h-4 text-blue-400" />
            </button>
          )}
          {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
            <button
              onClick={() => onPay(invoice)}
              className="p-1.5 rounded bg-neon-green/20 hover:bg-neon-green/30 transition-colors"
              title="Process Payment"
            >
              <CreditCard className="w-4 h-4 text-neon-green" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Rate Row Component
const RateRow = ({ rate, onEdit }) => (
  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
    <div className="flex items-center space-x-3">
      <MapPin className="w-4 h-4 text-slate-400" />
      <div>
        <div className="text-white text-sm font-medium">
          {rate.state} {rate.zip_prefix ? `(${rate.zip_prefix}xx)` : '(All)'}
        </div>
        <div className="text-xs text-slate-400 capitalize">{rate.trip_type}</div>
      </div>
    </div>
    <div className="flex items-center space-x-4">
      <div className="text-right">
        <div className="text-sm text-neon-cyan font-mono">${rate.rate_per_mile}/mi</div>
        <div className="text-xs text-slate-500">Min: ${rate.min_charge}</div>
      </div>
      <span className={`px-2 py-0.5 rounded text-xs ${rate.is_active ? 'bg-neon-green/20 text-neon-green' : 'bg-slate-600 text-slate-400'}`}>
        {rate.is_active ? 'Active' : 'Inactive'}
      </span>
    </div>
  </div>
);

// Generate Invoice Modal
const GenerateInvoiceModal = ({ isOpen, onClose, onGenerate }) => {
  const [periodType, setPeriodType] = useState('weekly');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [entityType, setEntityType] = useState('driver');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Set default dates (last week)
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    setPeriodStart(start.toISOString().split('T')[0]);
    setPeriodEnd(end.toISOString().split('T')[0]);
  }, []);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      await onGenerate({
        period_type: periodType,
        period_start: periodStart,
        period_end: periodEnd,
        billable_entity_type: entityType
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="glass-card p-6 max-w-md w-full"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
          <FileText className="w-5 h-5 mr-2 text-neon-cyan" />
          Generate Invoices
        </h3>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Billing Period</label>
            <select
              value={periodType}
              onChange={e => setPeriodType(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
            >
              <option value="per_trip">Per Trip</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Start Date</label>
              <input
                type="date"
                value={periodStart}
                onChange={e => setPeriodStart(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">End Date</label>
              <input
                type="date"
                value={periodEnd}
                onChange={e => setPeriodEnd(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-slate-400 mb-1 block">Bill To</label>
            <select
              value={entityType}
              onChange={e => setEntityType(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
            >
              <option value="driver">Individual Drivers</option>
              <option value="provider">Transportation Providers</option>
            </select>
          </div>

          <div className="flex space-x-3 pt-4">
            <button onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="btn-primary flex-1 flex items-center justify-center"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Generate'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Add Rate Modal
const AddRateModal = ({ isOpen, onClose, onAdd }) => {
  const [state, setState] = useState('');
  const [zipPrefix, setZipPrefix] = useState('');
  const [tripType, setTripType] = useState('wheelchair');
  const [ratePerMile, setRatePerMile] = useState('0.12');
  const [minCharge, setMinCharge] = useState('0.50');
  const [loading, setLoading] = useState(false);

  const handleAdd = async () => {
    if (!state) {
      toast.error('State is required');
      return;
    }
    
    setLoading(true);
    try {
      await onAdd({
        state: state.toUpperCase(),
        zip_prefix: zipPrefix || null,
        trip_type: tripType,
        rate_per_mile: parseFloat(ratePerMile),
        min_charge: parseFloat(minCharge),
        effective_date: new Date().toISOString().split('T')[0]
      });
      onClose();
      setState('');
      setZipPrefix('');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="glass-card p-6 max-w-md w-full"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
          <Plus className="w-5 h-5 mr-2 text-neon-green" />
          Add Insurance Rate
        </h3>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">State</label>
              <input
                type="text"
                value={state}
                onChange={e => setState(e.target.value.toUpperCase())}
                maxLength={2}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white uppercase"
                placeholder="IL"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">ZIP Prefix (opt)</label>
              <input
                type="text"
                value={zipPrefix}
                onChange={e => setZipPrefix(e.target.value)}
                maxLength={3}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
                placeholder="606"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-slate-400 mb-1 block">Trip Type</label>
            <select
              value={tripType}
              onChange={e => setTripType(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
            >
              <option value="wheelchair">Wheelchair</option>
              <option value="ambulatory">Ambulatory</option>
              <option value="stretcher">Stretcher</option>
              <option value="bariatric">Bariatric</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Rate per Mile ($)</label>
              <input
                type="number"
                step="0.01"
                value={ratePerMile}
                onChange={e => setRatePerMile(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Min Charge ($)</label>
              <input
                type="number"
                step="0.01"
                value={minCharge}
                onChange={e => setMinCharge(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
              />
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={loading}
              className="btn-primary flex-1 flex items-center justify-center"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Add Rate'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Component
export const InsuranceBillingDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Dashboard data
  const [dashboardStats, setDashboardStats] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  
  // Trips data
  const [trips, setTrips] = useState([]);
  const [tripFilter, setTripFilter] = useState({ billable: null });
  
  // Invoices data
  const [invoices, setInvoices] = useState([]);
  const [invoiceFilter, setInvoiceFilter] = useState('');
  
  // Rates data
  const [rates, setRates] = useState([]);
  
  // Modals
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showAddRateModal, setShowAddRateModal] = useState(false);

  // Fetch dashboard data
  const fetchDashboard = async () => {
    try {
      const [statsRes, revenueRes] = await Promise.all([
        fetch(`${API_URL}/api/insurance-billing/dashboard?period=month`),
        fetch(`${API_URL}/api/insurance-billing/dashboard/daily-revenue?days=14`)
      ]);
      
      if (statsRes.ok) {
        const stats = await statsRes.json();
        setDashboardStats(stats);
      }
      
      if (revenueRes.ok) {
        const revenue = await revenueRes.json();
        setRevenueData(revenue.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  };

  // Fetch trips
  const fetchTrips = async () => {
    try {
      let url = `${API_URL}/api/insurance-billing/trips?limit=50`;
      if (tripFilter.billable !== null) {
        url += `&billable=${tripFilter.billable}`;
      }
      
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setTrips(data.trips || []);
      }
    } catch (error) {
      console.error('Failed to fetch trips:', error);
    }
  };

  // Fetch invoices
  const fetchInvoices = async () => {
    try {
      let url = `${API_URL}/api/insurance-billing/invoices?limit=50`;
      if (invoiceFilter) {
        url += `&status=${invoiceFilter}`;
      }
      
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setInvoices(data.invoices || []);
      }
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    }
  };

  // Fetch rates
  const fetchRates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/insurance-billing/rates`);
      if (res.ok) {
        const data = await res.json();
        setRates(data.rates || []);
      }
    } catch (error) {
      console.error('Failed to fetch rates:', error);
    }
  };

  // Initial load
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchTrips(), fetchInvoices(), fetchRates()]);
      setLoading(false);
    };
    loadAll();
  }, []);

  // Refresh on tab change
  useEffect(() => {
    if (activeTab === 'dashboard') fetchDashboard();
    if (activeTab === 'trips') fetchTrips();
    if (activeTab === 'invoices') fetchInvoices();
    if (activeTab === 'rates') fetchRates();
  }, [activeTab, tripFilter, invoiceFilter]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchDashboard(), fetchTrips(), fetchInvoices(), fetchRates()]);
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleGenerateInvoices = async (params) => {
    try {
      const res = await fetch(`${API_URL}/api/insurance-billing/invoices/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      });
      
      const data = await res.json();
      if (data.invoices_created > 0) {
        toast.success(`Generated ${data.invoices_created} invoices`);
        fetchInvoices();
        fetchDashboard();
      } else {
        toast.info('No uninvoiced trips found for this period');
      }
    } catch (error) {
      toast.error('Failed to generate invoices');
    }
  };

  const handleAddRate = async (rateData) => {
    try {
      const res = await fetch(`${API_URL}/api/insurance-billing/rates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rateData)
      });
      
      if (res.ok) {
        toast.success('Rate added successfully');
        fetchRates();
      } else {
        toast.error('Failed to add rate');
      }
    } catch (error) {
      toast.error('Failed to add rate');
    }
  };

  const handlePayInvoice = async (invoice) => {
    try {
      const res = await fetch(`${API_URL}/api/insurance-billing/invoices/${invoice.invoice_id}/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoice_id: invoice.invoice_id })
      });
      
      if (res.ok) {
        toast.success('Payment processed');
        fetchInvoices();
        fetchDashboard();
      } else {
        toast.error('Payment failed');
      }
    } catch (error) {
      toast.error('Payment failed');
    }
  };

  const handleSendInvoice = async (invoice) => {
    try {
      const res = await fetch(`${API_URL}/api/insurance-billing/invoices/${invoice.invoice_id}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success(`Invoice sent to ${data.email_sent_to || 'customer'}`);
        fetchInvoices();
      } else {
        toast.error(data.detail || 'Failed to send invoice');
      }
    } catch (error) {
      toast.error('Failed to send invoice');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-neon-cyan animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white flex items-center">
            <DollarSign className="w-6 h-6 mr-2 text-neon-green" />
            Insurance Billing Suite
          </h1>
          <p className="text-slate-400 text-sm mt-1">Pay-per-mile captive revenue management</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn-secondary flex items-center"
            data-testid="refresh-billing-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowGenerateModal(true)}
            className="btn-primary flex items-center"
            data-testid="generate-invoices-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Generate Invoices
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 border-b border-white/10 pb-2">
        {[
          { id: 'dashboard', label: 'Dashboard', icon: PieChart },
          { id: 'trips', label: 'Trips', icon: Car },
          { id: 'invoices', label: 'Invoices', icon: FileText },
          { id: 'rates', label: 'Rates', icon: DollarSign }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
              activeTab === tab.id
                ? 'bg-neon-cyan/20 text-neon-cyan'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && dashboardStats && (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={Car}
              label="Billable Trips"
              value={dashboardStats.trips?.billable || 0}
              subValue={`${dashboardStats.trips?.cancelled || 0} cancelled`}
              color="neon-cyan"
            />
            <StatCard
              icon={TrendingUp}
              label="Total Miles"
              value={`${(dashboardStats.trips?.total_miles || 0).toLocaleString()}`}
              color="neon-purple"
            />
            <StatCard
              icon={DollarSign}
              label="Total Charges"
              value={`$${(dashboardStats.trips?.total_charges_usd || 0).toFixed(2)}`}
              color="neon-green"
            />
            <StatCard
              icon={CreditCard}
              label="Revenue Collected"
              value={`$${(dashboardStats.payments?.revenue_usd || 0).toFixed(2)}`}
              color="neon-yellow"
            />
          </div>

          {/* Invoice Status */}
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Invoice Status</h3>
              <div className="space-y-3">
                {['draft', 'sent', 'paid', 'overdue'].map(status => {
                  const data = dashboardStats.invoices?.[status] || { count: 0, total: 0 };
                  const colors = invoiceStatusColors[status];
                  return (
                    <div key={status} className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className={`w-3 h-3 rounded-full ${colors.bg}`} />
                        <span className="text-slate-300 capitalize">{status}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-white font-medium">{data.count}</span>
                        <span className="text-slate-500 ml-2">(${data.total?.toFixed(2) || '0.00'})</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Daily Revenue Chart (simple) */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Daily Revenue (14 days)</h3>
              <div className="flex items-end space-x-1 h-32">
                {revenueData.slice(-14).map((day, idx) => {
                  const maxRevenue = Math.max(...revenueData.map(d => d.revenue), 1);
                  const height = (day.revenue / maxRevenue) * 100;
                  return (
                    <div
                      key={idx}
                      className="flex-1 bg-neon-green/30 rounded-t hover:bg-neon-green/50 transition-colors relative group"
                      style={{ height: `${height}%`, minHeight: '4px' }}
                    >
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 px-2 py-1 rounded text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                        ${day.revenue.toFixed(2)}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2 text-xs text-slate-500">
                <span>{revenueData[0]?.date || ''}</span>
                <span>{revenueData[revenueData.length - 1]?.date || ''}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trips Tab */}
      {activeTab === 'trips' && (
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <select
              value={tripFilter.billable === null ? '' : tripFilter.billable.toString()}
              onChange={e => setTripFilter({ 
                billable: e.target.value === '' ? null : e.target.value === 'true' 
              })}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="">All Trips</option>
              <option value="true">Billable Only</option>
              <option value="false">Not Billable</option>
            </select>
            <span className="text-slate-400 text-sm">{trips.length} trips</span>
          </div>

          <div className="glass-card p-4 space-y-2 max-h-[500px] overflow-y-auto">
            {trips.length > 0 ? (
              trips.map(trip => <TripRow key={trip.trip_charge_id} trip={trip} />)
            ) : (
              <div className="text-center py-12 text-slate-400">
                <Car className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No trips found</p>
                <p className="text-sm">Completed trips from OVA Dispatch will appear here</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <select
              value={invoiceFilter}
              onChange={e => setInvoiceFilter(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="">All Invoices</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
            </select>
            <span className="text-slate-400 text-sm">{invoices.length} invoices</span>
          </div>

          <div className="glass-card p-4 space-y-2 max-h-[500px] overflow-y-auto">
            {invoices.length > 0 ? (
              invoices.map(invoice => (
                <InvoiceRow
                  key={invoice.invoice_id}
                  invoice={invoice}
                  onView={(inv) => console.log('View invoice:', inv)}
                  onPay={handlePayInvoice}
                  onSend={handleSendInvoice}
                />
              ))
            ) : (
              <div className="text-center py-12 text-slate-400">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No invoices found</p>
                <p className="text-sm">Generate invoices from completed trips</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Rates Tab */}
      {activeTab === 'rates' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm">{rates.length} rates configured</span>
            <button
              onClick={() => setShowAddRateModal(true)}
              className="btn-secondary flex items-center text-sm"
              data-testid="add-rate-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Rate
            </button>
          </div>

          <div className="glass-card p-4 space-y-2 max-h-[500px] overflow-y-auto">
            {rates.length > 0 ? (
              rates.map(rate => <RateRow key={rate.rate_id} rate={rate} />)
            ) : (
              <div className="text-center py-12 text-slate-400">
                <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No rates configured</p>
                <p className="text-sm">Add area-based rates from ITR</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {showGenerateModal && (
          <GenerateInvoiceModal
            isOpen={showGenerateModal}
            onClose={() => setShowGenerateModal(false)}
            onGenerate={handleGenerateInvoices}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAddRateModal && (
          <AddRateModal
            isOpen={showAddRateModal}
            onClose={() => setShowAddRateModal(false)}
            onAdd={handleAddRate}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default InsuranceBillingDashboard;
